import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from 'recharts';
import { Building2, TrendingUp, Coins, Receipt, ArrowUpRight, Wallet } from 'lucide-react';

/**
 * Platform-admin revenue dashboard.
 * Aggregates Stripe checkout.session.completed audit-log rows from
 * `/platform/revenue` and renders MRR + per-tenant rankings + upgrade hints.
 */
const PERIODS = [
  { id: 'mtd', label: 'MTD' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: 'ytd', label: 'YTD' },
  { id: 'all', label: 'All time' },
];

export default function PlatformRevenue() {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/platform/revenue?period=${period}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [period]);

  const fmt = (cents) => ((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">PLATFORM</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Tenant revenue</h1>
          <p className="text-fg-muted mt-2">Application fees collected across all clinics on Physiogenic.</p>
        </div>
        <div className="flex items-center gap-2 surface border border-soft rounded-lg p-1" data-testid="period-selector">
          {PERIODS.map(p => (
            <button key={p.id}
                    onClick={() => setPeriod(p.id)}
                    className={`text-xs px-3 py-1.5 rounded-md transition ${period === p.id ? 'bg-brand text-white' : 'text-fg-muted hover:text-fg'}`}
                    data-testid={`period-${p.id}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-center py-12 text-fg-muted">Loading…</div>}

      {!loading && data && (
        <>
          {/* KPI tiles */}
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <Tile icon={Coins} label="Gross revenue" value={`${fmt(data.totals.gross_revenue_cents)} ${data.currency}`} testid="kpi-gross"/>
            <Tile icon={Wallet} label="Platform fees" value={`${fmt(data.totals.platform_fee_cents)} ${data.currency}`} testid="kpi-fees" accent/>
            <Tile icon={Receipt} label="Transactions" value={data.totals.transactions} testid="kpi-txn"/>
            <Tile icon={Building2} label="Active clinics" value={data.totals.active_clinics} testid="kpi-clinics"/>
          </div>

          {/* Empty state when no data */}
          {data.totals.transactions === 0 ? (
            <div className="surface border border-soft rounded-xl p-12 text-center" data-testid="revenue-empty">
              <TrendingUp className="w-12 h-12 mx-auto text-fg-muted/40"/>
              <h3 className="font-heading font-semibold mt-4">No revenue yet for this period</h3>
              <p className="text-sm text-fg-muted mt-2 max-w-md mx-auto">
                Revenue rows are written when Stripe Checkout sessions complete. Make sure the Stripe webhook is configured at
                <code className="text-brand bg-soft px-1.5 py-0.5 rounded mx-1">/api/stripe/webhook</code>
                with the <code className="text-brand bg-soft px-1.5 py-0.5 rounded mx-1">STRIPE_WEBHOOK_SECRET</code> env var set.
              </p>
            </div>
          ) : (
            <>
              {/* Daily trend chart */}
              <div className="surface border border-soft rounded-xl p-5 mb-6">
                <h3 className="font-heading font-semibold mb-3 inline-flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-brand"/> Daily revenue trend
                </h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.daily.map(d => ({
                      date: d.date.slice(5),
                      gross: d.gross_cents / 100,
                      fee: d.platform_fee_cents / 100,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E8DFC8"/>
                      <XAxis dataKey="date" stroke="#6B6F6B" fontSize={11}/>
                      <YAxis stroke="#6B6F6B" fontSize={11}/>
                      <Tooltip
                        formatter={(v) => `${v.toFixed(2)} ${data.currency}`}
                        contentStyle={{ background: '#fff', border: '1px solid #E8DFC8', borderRadius: 8, fontSize: 12 }}/>
                      <Line type="monotone" dataKey="gross" stroke="#003D80" strokeWidth={2} dot={false} name="Gross"/>
                      <Line type="monotone" dataKey="fee" stroke="#FFB000" strokeWidth={2} dot={false} name="Platform fee"/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Top clinics table */}
              <div className="surface border border-soft rounded-xl p-5">
                <h3 className="font-heading font-semibold mb-3 inline-flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-brand"/> Top-grossing clinics
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-fg-muted border-b border-soft">
                      <tr>
                        <th className="text-left py-2">Clinic</th>
                        <th className="text-left">Plan</th>
                        <th className="text-right">Txns</th>
                        <th className="text-right">Gross</th>
                        <th className="text-right">Platform fee</th>
                        <th className="text-right">Est. MRR fee</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.clinics.map(c => (
                        <tr key={c.clinic_id} className="border-b border-soft/40" data-testid={`rev-row-${c.clinic_id}`}>
                          <td className="py-3">
                            <div className="font-medium">{c.name}</div>
                            <div className="text-[11px] text-fg-muted">{c.slug || c.clinic_id}</div>
                          </td>
                          <td><span className="badge-neutral uppercase text-[10px]">{c.plan}</span></td>
                          <td className="text-right font-mono">{c.transactions}</td>
                          <td className="text-right font-mono">{fmt(c.gross_cents)} {data.currency}</td>
                          <td className="text-right font-mono text-[#FFB000] font-semibold">{fmt(c.platform_fee_cents)}</td>
                          <td className="text-right font-mono">{fmt(c.mrr_estimate_cents)}</td>
                          <td>
                            {c.upgrade_hint && (
                              <button
                                className="text-xs text-brand inline-flex items-center gap-1 hover:underline"
                                title={`Recommend upgrading to ${c.upgrade_hint}`}
                                data-testid={`upgrade-hint-${c.clinic_id}`}
                              >
                                Suggest {c.upgrade_hint} <ArrowUpRight className="w-3 h-3"/>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Period note */}
              <p className="text-[11px] text-fg-muted mt-4">
                Period spans {data.totals.period_days} days. Est. MRR projects this period's platform fees to a 30-day month.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, testid, accent }) {
  return (
    <div className={`surface border border-soft rounded-xl p-4 ${accent ? 'bg-[#FFF4D6]' : ''}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-fg-muted mb-1 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5"/> {label}
      </div>
      <div className="text-2xl font-heading font-bold text-fg">{value}</div>
    </div>
  );
}
